import { z } from 'zod';
import type {
  PathPackage,
  PathPackageSubPath,
  PathRelative,
  Branded,
} from './re-exports';
import type {
  KnapsackPatternTemplate,
  TemplateDemo,
  TemplateRendererMeta,
  KsSpecMeta,
  KsTemplateSpec,
} from './app-client-data/patterns';
import type { Simplify } from './type-utils';

export const rendererIds = {
  react: 'react',
  vue: 'vue',
  'web-components': 'web-components',
  twig: 'twig',
  hbs: 'hbs',
  angular: 'angular',
  html: 'html',
} as const;

/**
 * The ID of a renderer, e.g. 'react', 'vue', 'angular', etc.
 * On Pattern Templates, this will match up to `templateLanguageId` to identify which renderer to use.
 * @see {KnapsackPatternTemplate['templateLanguageId']}
 */
export type RendererId = keyof typeof rendererIds;
export type CodeSrc =
  | {
      type: 'package';
      path: PathPackage;
      pathFromOutputDir: PathPackage;
      pkgName: PathPackage;
      rendererId: RendererId;
    }
  | {
      type: 'package-sub-path';
      path: PathPackageSubPath;
      pathFromOutputDir: PathPackageSubPath;
      pkgName: PathPackage;
      subPath: PathRelative;
      rendererId: RendererId;
    }
  | {
      type: 'relative-from-data-dir';
      rendererId: RendererId;
      path: PathRelative;
      pathFromOutputDir: PathRelative;
    };

export type CodeSrcUserConfig = {
  /**
   * Can contain glob patterns, relative paths, absolute paths, package paths, or package sub-paths
   */
  path: string;
  filter?: (path: string) => boolean;
};

export const CodeSrcUserConfigSchema = z.strictObject({
  path: z
    .string()
    .describe(
      'Can contain glob patterns, relative paths, absolute paths, package paths, or package sub-paths',
    ),
  filter: z
    .custom<(path: string) => boolean>((val) => {
      return typeof val === 'function';
    })
    .optional()
    .describe('A function that takes a path and returns a boolean'),
});

export type TemplateInfoId = Branded<string, 'TemplateInfoId'>;

export type TemplateInfo<TheRendererId extends RendererId = RendererId> =
  Extract<
    | {
        rendererId: 'react';
        codeSrcPath: CodeSrc['path'];
        /**
         * Name of the export. If it's the default export, this will be `'default'`.
         */
        exportName: string;
      }
    | {
        rendererId: 'vue';
        codeSrcPath: CodeSrc['path'];
      }
    | {
        rendererId: 'web-components';
        elementName: string;
      }
    | {
        rendererId: 'angular';
        codeSrcPath: CodeSrc['path'];
        /**
         * Name of the export. If it's the default export, this will be `'default'`.
         */
        exportName: string;
      }
    | {
        rendererId: 'html';
        codeSrcPath: CodeSrc['path'];
      }
    | {
        rendererId: 'twig';
        namespacePath: string;
      }
    | {
        rendererId: 'hbs';
        codeSrcPath: CodeSrc['path'];
      },
    { rendererId: TheRendererId }
  >;

export type TemplateInfoWithId<TheRendererId extends RendererId = RendererId> =
  Extract<
    Simplify<
      TemplateInfo<TheRendererId> & {
        id: TemplateInfoId;
      }
    >,
    { rendererId: TheRendererId }
  >;

export type TemplateInfoWithCodeSrcPath = Extract<
  TemplateInfo,
  { codeSrcPath: string }
>;

export function isTemplateInfoWithCodeSrcPath(
  info: TemplateInfo,
): info is TemplateInfoWithCodeSrcPath {
  return 'codeSrcPath' in info;
}

/**
 * Create a unique ID for a template info
 * This is used to identify the template info by a single unique string
 * @returns A unique ID for the template info
 */
export function createTemplateInfoId(t: TemplateInfo): TemplateInfoId {
  switch (t.rendererId) {
    case 'angular':
    case 'react':
      return `${t.rendererId}---${t.codeSrcPath}---${
        t.exportName || 'default'
      }` as TemplateInfoId;
    case 'web-components':
      return `${t.rendererId}---${t.elementName}` as TemplateInfoId;
    case 'twig':
      return `${t.rendererId}---${t.namespacePath}` as TemplateInfoId;
    case 'vue':
    case 'hbs':
    case 'html':
      return `${t.rendererId}---${t.codeSrcPath}` as TemplateInfoId;
    default: {
      const _: never = t;
      throw new Error(`Invalid template info: ${JSON.stringify(t)}`);
    }
  }
}

export function addIdToTemplateInfo<TheRendererId extends RendererId>(
  t: TemplateInfo<TheRendererId>,
): TemplateInfoWithId<TheRendererId> {
  return {
    ...t,
    id: createTemplateInfoId(t),
  };
}

/**
 * Converts old way of specifying template info (`path` & `alias`) to new way {@link TemplateInfo}
 * @see {@link convertTemplateInfoToOldWay} for the reverse
 */
export function normalizeTemplateInfo<TheRendererId extends RendererId>({
  rendererId,
  alias,
  path,
}: {
  rendererId: TheRendererId;
  path?: CodeSrc['path'];
  alias?: string;
}): TemplateInfo<TheRendererId> {
  const info = ((): TemplateInfo => {
    switch (rendererId) {
      case 'react':
      case 'angular':
        return {
          rendererId,
          codeSrcPath: path,
          exportName: alias || 'default',
        };
      case 'vue':
      case 'hbs':
      case 'html':
        return {
          rendererId,
          codeSrcPath: path,
        };
      case 'twig':
        return {
          rendererId,
          namespacePath: alias,
        };
      case 'web-components':
        return {
          rendererId,
          elementName: alias,
        };
      default: {
        const _: never = rendererId;
        throw new Error(`Invalid renderer ID: ${rendererId}`);
      }
    }
  })();

  return info as TemplateInfo<TheRendererId>;
}

export type RendererTemplateConfig = {
  templateInfo: TemplateInfo;
  /**
   * If it's stored here, it's never inferred
   */
  spec?: KsTemplateSpec<false | undefined>;
  specMeta?: KsSpecMeta;
};

/**
 * Converts new way of specifying template info {@link TemplateInfo} to old way (`path` & `alias`)
 * @see {@link normalizeTemplateInfo} for the reverse
 */
export function convertTemplateInfoToOldWay(info: TemplateInfo): {
  path?: string;
  alias?: string;
} {
  switch (info.rendererId) {
    case 'react':
    case 'angular':
      return {
        path: info.codeSrcPath,
        alias: info.exportName,
      };
    case 'hbs':
    case 'html':
    case 'vue':
      return {
        path: info.codeSrcPath,
      };
    case 'twig':
      return {
        alias: info.namespacePath,
      };
    case 'web-components':
      return {
        alias: info.elementName,
      };
    default: {
      const _: never = info;
      throw new Error(`Invalid template info: ${JSON.stringify(info)}`);
    }
  }
}

export type RendererDiscovery<TheRendererId extends RendererId> = {
  rendererId: TheRendererId;
  meta: TemplateRendererMeta;
  codeSrcsByPath: Record<CodeSrc['path'], CodeSrc>;
  templates: TemplateInfoWithId<TheRendererId>[];
};

/**
 * Results of rendering a pattern
 */
export type KsRenderResults = {
  ok: boolean;
  html: string;
  usage?: string;
  templateLanguage?: string;
  message?: string;
};

export type KsRenderApiResults = KsRenderResults & {
  wrappedHtml: string;
};
export type TemplateSuggestion = {
  path: string;
  alias?: string;
  connected?: boolean;
};

export type TemplateSuggestionsGetFn = (opt: {
  rendererId: RendererId;
  newPath?: string;
  state: ContentStateForRendering;
}) => Promise<{
  suggestions: TemplateSuggestion[];
}>;

/**
 * Aliases for package paths
 * Example:
 * ```js
 * {
 *   'my-pkg': '../path/to/my-pkg',
 * }
 * ```
 */
export type PkgPathAliases = Record<string, string>;

export type ContentStateForRendering = {
  /**
   * Patterns by ID
   */
  patterns: {
    [patternId: string]: {
      id: string;
      templates: Pick<
        KnapsackPatternTemplate,
        'alias' | 'path' | 'templateLanguageId' | 'spec' | 'id' | 'title'
      >[];
      templateDemos: Array<
        TemplateDemo & {
          templateId: string;
          templateLanguageId: RendererId;
        }
      >;
    };
  };
};

export type TemplateUse = {
  patternId: string;
  patternTitle: string;
  templateId: string;
  templateTitle: string;
};

export type PathWithTemplates = {
  path: string;
  templates: {
    id: TemplateInfoId;
    templateInfo: TemplateInfoWithId;
    use: TemplateUse;
    suggestion: TemplateSuggestion;
  }[];
};
